import React, { useRef, useState } from "react";
import { useLang, pick } from "../i18n/LanguageContext";
import { t } from "../i18n/translations";
import ViewportVideo from "./ViewportVideo";

/**
 * Block — videon/kuvan + otsikko/leipäteksti -ruudukon yhdistelmä.
 *
 * Käytetään casejen, filmiprojektien ja missio-osion esittelyssä.
 * Layout: media (video tai kuva) yläpuolella koko leveydeltä, alla 4-sarakkeen
 * grid (otsikko / alaotsikko / 2 leipätekstipalstaa).
 *
 * Videoissa on äänen päälle/pois -nappi oikeassa alanurkassa. Videot
 * toistuvat vain näkymässä ja näyttävät siihen asti posterin (ViewportVideo).
 *
 * Props:
 *  - media?: { type: "video" | "image", src, alt?, width?, height? }  // valinnainen
 *      | { type: "carousel", items: [{ src, label?, ratio? }] }
 *        → useampi video, vaihto nuolilla; ratio "9 / 16" näytetään
 *          16:9-kehyksessä kokonaan (contain) tummalla taustalla
 *  - headline?: string | string[]                          // teksti tai rivit
 *  - subheadline?: string
 *  - body?: string
 *  - body2?: string
 *  - id?: string                                           // anchor
 *  - gallery?: Array<Array<{ src, ratio, alt?, type?, width?, height? }>>
 *      kuva-/videorivit tekstin alle
 *      type: "video" → mykkä looppaava video ilman äänipainiketta
 *      width/height = kuvatiedoston pikselikoko (tilanvaraus ennen latausta)
 */

const SoundToggle = ({ src }) => {
  const { lang } = useLang();
  const tr = pick(lang);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const handleToggle = () => {
    const next = !muted;
    setMuted(next);
    if (videoRef.current) {
      videoRef.current.muted = next;
      if (!next) {
        videoRef.current.play().catch(() => {});
      }
    }
  };

  return (
    <button
      type="button"
      className="block__video"
      onClick={handleToggle}
      aria-label={tr(muted ? t.tyot.soundOn : t.tyot.soundOff)}
      aria-pressed={!muted}
      data-muted={muted ? "true" : "false"}
    >
      <ViewportVideo src={src} videoRef={videoRef} tapToPlay={false} />
    </button>
  );
};

/**
 * Carousel — useampi video samassa 16:9-kehyksessä, vaihto nuolilla.
 * Aktiivinen video remountataan (key=src), jotta toisto ja mykistys
 * alkavat puhtaalta pöydältä jokaisella vaihdolla: uusi video käynnistyy
 * mykkänä alusta, kun karuselli on näkymässä.
 */
const Carousel = ({ items }) => {
  const { lang } = useLang();
  const tr = pick(lang);
  const [index, setIndex] = useState(0);
  const count = items.length;
  const go = (delta) => setIndex((i) => (i + delta + count) % count);
  const item = items[index];
  const portrait = item.ratio && item.ratio.replace(/\s/g, "") === "9/16";

  return (
    <>
      <div className={`block__media${portrait ? " block__media--contain" : ""}`}>
        <SoundToggle key={item.src} src={item.src} />
      </div>
      <div className="block__carousel-nav">
        <span className="block__carousel-label">{item.label}</span>
        <span className="block__carousel-count" aria-live="polite">
          {index + 1} / {count}
        </span>
        <div className="block__carousel-arrows">
          <button
            type="button"
            className="block__carousel-arrow"
            onClick={() => go(-1)}
            aria-label={tr(t.tyot.prevVideo)}
          >
            ←
          </button>
          <button
            type="button"
            className="block__carousel-arrow"
            onClick={() => go(1)}
            aria-label={tr(t.tyot.nextVideo)}
          >
            →
          </button>
        </div>
      </div>
    </>
  );
};

const Block = ({ media, headline, subheadline, body, body2, id, specs, gallery }) => {
  const headlineLines = Array.isArray(headline)
    ? headline
    : headline
    ? [headline]
    : [];

  return (
    <section className="block" id={id}>
      {media && media.type === "portrait-grid" && (
        <div className="block__media block__media--portrait-grid">
          {media.srcs.map((src, i) => (
            <SoundToggle key={i} src={src} />
          ))}
        </div>
      )}

      {media && media.type === "carousel" && <Carousel items={media.items} />}

      {media && media.type !== "portrait-grid" && media.type !== "carousel" && (
        <div className="block__media">
          {media.type === "video" && <SoundToggle src={media.src} />}
          {media.type === "image" && (
            <img
              src={media.src}
              alt={media.alt || ""}
              width={media.width}
              height={media.height}
              loading="lazy"
            />
          )}
        </div>
      )}

      <div className="block__grid">
        {headlineLines.length > 0 && (
          <h2 className="block__headline">
            {headlineLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </h2>
        )}

        {subheadline && <p className="block__subheadline">{subheadline}</p>}

        {body && <p className="block__body">{body}</p>}

        {body2 && <p className="block__body">{body2}</p>}
      </div>

      {gallery && gallery.length > 0 && (
        <div className="block__gallery">
          {gallery.map((row, ri) => (
            <div key={ri} className="block__gallery-row">
              {row.map((img) => (
                <figure
                  key={img.src}
                  className="block__gallery-item"
                  style={{ "--ratio": img.ratio || "4 / 3" }}
                >
                  {img.type === "video" ? (
                    // Mykkä dokumentaatiovideo kuvien seassa — ei ääntä,
                    // ei äänipainiketta, pyörii loopilla näkymässä.
                    <ViewportVideo src={img.src} />
                  ) : (
                    <img
                      src={img.src}
                      alt={img.alt || ""}
                      width={img.width}
                      height={img.height}
                      loading="lazy"
                    />
                  )}
                </figure>
              ))}
            </div>
          ))}
        </div>
      )}

      {specs && specs.length > 0 && (
        <dl className="block__specs">
          {specs.map((s) => (
            <div key={s.label} className="block__spec">
              <dt>{s.label}</dt>
              <dd>{s.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
};

export default Block;
